import {SiteActionBar} from '@/pages/site/SiteActionBar.tsx'
import SiteBulk from '@/pages/site/SiteBulk'
import {Switch} from 'antd'
import {ListResponseSiteBean, SiteBean} from 'common/api/api'
import siteApi from 'common/api/SiteApi.ts'
import {ListCard} from 'common/components/ListCard.tsx'
import {PublisherSelector} from 'common/components/reloadable/PublisherSelector.tsx'
import {ListFilters, useAppContext} from 'common/context/AppContext.tsx'
import {CommonRoutes} from 'common/context/commonRoutes'
import {initListFilters, useListFilters} from 'common/util/useListFilters.ts'
import {apiCall, showError} from 'common/util/Util.tsx'
import * as React from 'react'
import {useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import dayjs from 'dayjs'
import {COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH} from 'common/util/constants.ts'

const SiteList: React.FC = () => {
    const [isBulk, setIsBulk] = useState(false)

    const [listData, setListData] = useState<ListResponseSiteBean>()
    const appContext = useAppContext()
    const [loading, setLoading] = useState<boolean>(false)
    const {pubId} = useParams<{pubId?: string}>()
    const [filters, setFilters] = useState<ListFilters>(initListFilters({
        publisherId: pubId ? Number(pubId) : undefined, sort: {name: 'id', direction: 'desc'}}))
    const {setFilter} = useListFilters(setFilters)

    const loadData = () => {
        apiCall(setLoading, appContext,
            () => siteApi.list(
                filters.sort?.name,
                filters.sort?.direction,
                filters.name,
                filters.active,
                filters.publisherId,
                { query: { page: filters.page, size: filters.pageSize } }
            ),
            (bean: ListResponseSiteBean) => setListData(bean)
        )
    }

    const toggleBulk = () => setIsBulk(!isBulk)

    const onError = e => {
        setLoading(false)
        showError(appContext, e)
    }

    const handleCsv = () => {
        siteApi.getCsv('SiteList.csv').catch(onError)
    }


    const toggleActive = (id: number | undefined, record: SiteBean, checked: boolean) => {
        if (id === undefined) return
        apiCall(setLoading, appContext,
            () => siteApi.setActive(id, checked),
            () => {
                record.active = checked
                setListData(listData)
            })
    }

    return (
        <ListCard
            loading={loading}
            title="Sites"
            listRouteKey={CommonRoutes.SITE}
            filters={filters}
            setFilters={setFilters}
            loadData={loadData}
            listData={listData}
            createName="Site"
            createRoute={CommonRoutes.SITE_CREATE}
            additionalButtons={[
                {
                    name: 'Create bulk',
                    onClick: () => toggleBulk()
                },
                {
                    name: 'Export Raw Data',
                    onClick: () => handleCsv()
                }
            ]}
            columns={[
                {
                    title: 'ID',
                    dataIndex: 'id',
                    width: COLUMN_ID_WIDTH,
                    fixed: 'left',
                },
                {
                    title: 'Name',
                    dataIndex: 'name',
                    className: 'list-table-name',
                    sorter: true,
                    ellipsis: true,
                    render: (text, record) => (<Link style={{ color: '#5888CF', minWidth: 100 }}
                                                     to={`${CommonRoutes.SITE}/${record.id}/edit`}>{text}</Link>),
                    width: COLUMN_NAME_WIDTH,
                    fixed: 'left',
                },
                {
                    key: 'domain',
                    title: 'Domain',
                    dataIndex: 'domain',
                    className: 'list-table-name',
                    sorter: true,
                    ellipsis: true
                },
                {
                    title: "Last Modified",
                    dataIndex: "modified",
                    sorter: true,
                    width: 100,
                    render: (date) => date && dayjs(date).format('DD MMM YYYY')
                },
                {
                    title: 'Active',
                    dataIndex: 'active',
                    sorter: true,
                    render: (val, record) => (
                        <Switch
                            size="small"
                            checked={record.active}
                            onChange={(checked) => toggleActive(record.id, record, checked)}
                        />
                    ),
                    width: 60
                },
                {
                    key: 'action',
                    title: 'Actions',
                    render: (_text: string, record: SiteBean) => (
                        <SiteActionBar
                            record={record}
                        />
                    ),
                    width: 240
                }

            ]}
        >
            <PublisherSelector
                placeholder="Filter by Publisher"
                style={{minWidth: '200px'}}
                value={filters.publisherId}
                onChange={(value) => setFilter('publisherId', value)}
            />
            <SiteBulk visible={isBulk} toggle={toggleBulk} />
        </ListCard>
    )
}

export default SiteList
